import { computeEMA } from './chart/computeIndicator.js'
import { toChartTimestamp } from './timezone.js'
import { isUsEquityEarlyCloseDay, isUsEquityTradingDay } from './marketSession.js'

const DAY_KEY = /^\d{8}$/
const EASTERN_CLOCK = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
})

// Zero means today's RTH session has closed; null means no session today.
// Provider availability is separate: this never manufactures a closing bar.
export function millisecondsUntilDailyClose(now = new Date()) {
  if (!isUsEquityTradingDay(now)) return null
  const parts = EASTERN_CLOCK.formatToParts(now)
  const read = (type) => Number(parts.find((part) => part.type === type)?.value)
  const elapsed = (read('hour') * 60 + read('minute')) * 60000 + now.getUTCSeconds() * 1000 + now.getUTCMilliseconds()
  const close = (isUsEquityEarlyCloseDay(now) ? 13 : 16) * 3600000
  return Math.max(0, close - elapsed)
}

function easternDayKey(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)
  const read = (type) => parts.find((part) => part.type === type)?.value
  return `${read('year')}${read('month')}${read('day')}`
}

function normalizeDayKey(value) {
  const raw = String(value ?? '').trim()
  if (DAY_KEY.test(raw)) return raw
  const compact = raw.match(/^(\d{8})(?:\s|$)/)?.[1]
  if (compact) return compact
  const numeric = Number(value)
  if (!Number.isFinite(numeric) || numeric <= 0) return null
  const milliseconds = numeric > 10_000_000_000 ? numeric : numeric * 1000
  const parsed = new Date(milliseconds)
  return Number.isFinite(parsed.getTime()) ? easternDayKey(parsed) : null
}

function finiteNumber(value) {
  if (value == null || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

export function completedDailyCloses(bars, now = new Date(), includeForming = false) {
  const currentEasternDay = easternDayKey(now)
  const todayClosed = millisecondsUntilDailyClose(now) === 0
  const byDay = new Map()
  for (const bar of Array.isArray(bars) ? bars : []) {
    const date = normalizeDayKey(bar?.time)
    const close = finiteNumber(bar?.close)
    if (!date || close == null || close <= 0 || date > currentEasternDay || (date === currentEasternDay && !(todayClosed || (includeForming && isUsEquityTradingDay(now))))) continue
    byDay.set(date, close)
  }
  return [...byDay.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([date, close]) => ({ date, close }))
}

function rollingMean(values, period) {
  const result = Array(values.length).fill(null)
  for (let index = period - 1; index < values.length; index += 1) {
    const window = values.slice(index - period + 1, index + 1)
    if (window.every((value) => Number.isFinite(value))) {
      result[index] = window.reduce((sum, value) => sum + value, 0) / period
    }
  }
  return result
}

function unionDailySeries(left, right) {
  const leftByDay = new Map(left.map(({ date, close }) => [date, close]))
  const rightByDay = new Map(right.map(({ date, close }) => [date, close]))
  return [...new Set([...leftByDay.keys(), ...rightByDay.keys()])]
    .sort()
    .map((date) => ({ date, left: leftByDay.get(date) ?? null, right: rightByDay.get(date) ?? null }))
}

export function computeXlpQqqSeries(xlpBars, qqqBars, { now = new Date(), smaLength = 50, displaySessions = Infinity, includeForming = false } = {}) {
  // Same-date legs only. Live presentation includes the provisional RTH bar;
  // completed-session consumers retain the close boundary by default.
  const aligned = unionDailySeries(completedDailyCloses(xlpBars, now, includeForming), completedDailyCloses(qqqBars, now, includeForming))
  const ratios = aligned.map(({ left, right }) => (
    Number.isFinite(left) && Number.isFinite(right) && right !== 0 ? left / right : null
  ))
  const averages = rollingMean(ratios, smaLength)
  const full = aligned.map(({ date }, index) => {
    const ratio = ratios[index]
    const sma = averages[index]
    const previousRatio = ratios[index - 1]
    const previousSma = averages[index - 1]
    const comparable = Number.isFinite(ratio) && Number.isFinite(sma)
    const previousComparable = Number.isFinite(previousRatio) && Number.isFinite(previousSma)
    return {
      date,
      ratio,
      sma,
      inputComplete: Number.isFinite(ratio),
      smaComplete: Number.isFinite(sma),
      aboveSma: comparable ? ratio > sma : null,
      aboveSmaBand: comparable && ratio > sma ? 1 : null,
      crossedAbove: comparable && previousComparable ? previousRatio <= previousSma && ratio > sma : false,
      crossedBelow: comparable && previousComparable ? previousRatio > previousSma && ratio <= sma : false,
    }
  })
  const complete = full.filter((point) => point.inputComplete)
  if (!complete.length) throw new Error('No aligned completed XLP and QQQ sessions')
  return full.slice(-displaySessions)
}

export function computeJnkSeries(jnkBars, { now = new Date(), emaLength = 20, displaySessions = Infinity, includeForming = false } = {}) {
  const closes = completedDailyCloses(jnkBars, now, includeForming)
  if (!closes.length) throw new Error('No JNK sessions')
  // Seed and recurrence are shared with Position Workspace. Recompute from
  // canonical daily bars so a forming-bar replacement never compounds the EMA.
  const averages = new Map(computeEMA(closes.map(({ date, close }) => ({ time: toChartTimestamp(date), close })), emaLength)
    .map(({ time, value }) => [time, value]))
  return closes.map(({ date, close }, index) => {
    const ema = averages.get(toChartTimestamp(date)) ?? null
    const previous = index ? averages.get(toChartTimestamp(closes[index - 1].date)) : null
    const emaTrend = Number.isFinite(ema) && Number.isFinite(previous) ? Math.sign(ema - previous) : 0
    return { date, close, ema, emaTrend }
  }).slice(-displaySessions)
}

export function withChartRightPadding(series, count = 5) {
  return [
    ...series,
    ...Array.from({ length: count }, (_, index) => ({
      date: `spacer-${index + 1}`,
      isSpacer: true,
    })),
  ]
}

export function buildBackgroundRuns(series, selectDirection) {
  const runs = []
  let index = 0
  while (index < series.length) {
    const direction = Number(selectDirection(series[index])) || 0
    if (direction === 0 || series[index]?.isSpacer) {
      index += 1
      continue
    }
    const start = index
    while (
      index + 1 < series.length
      && !series[index + 1]?.isSpacer
      && (Number(selectDirection(series[index + 1])) || 0) === direction
    ) index += 1
    const endBoundary = series[Math.min(index + 1, series.length - 1)]?.date || series[index].date
    runs.push({
      direction,
      x1: series[start].date,
      x2: endBoundary,
    })
    index += 1
  }
  return runs
}

export function latestCompletePoint(series, requiredFields) {
  for (let index = series.length - 1; index >= 0; index -= 1) {
    const point = series[index]
    if (requiredFields.every((field) => Number.isFinite(point?.[field]))) return point
  }
  return null
}

export function formatMarketRegimeDate(dayKey) {
  if (!DAY_KEY.test(String(dayKey || ''))) return '—'
  const year = Number(dayKey.slice(0, 4))
  const month = Number(dayKey.slice(4, 6)) - 1
  const day = Number(dayKey.slice(6, 8))
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year, month, day)))
}
